import { useSyncExternalStore } from "react";

/**
 * Громкость плееров «Монтажа» — одна на все ролики и запоминается.
 *
 * Модератор за день смотрит десятки роликов, и выставлять звук на каждом
 * заново — раздражает (просьба 07.10.2026). Поставил раз — так и стоит на
 * всех видео, в том числе после перезахода.
 *
 * Живёт в localStorage этого браузера: это удобство конкретного человека, а
 * не данные. Хранилище бывает недоступно (приватный режим) — тогда просто
 * помним до перезагрузки страницы.
 *
 * На iPhone сайт громкость не меняет вовсе — её задают кнопки телефона. Там
 * запоминается только «звук выключен».
 */

export interface SavedVolume {
  /** 0…1 */
  volume: number;
  muted: boolean;
  /** Скорость воспроизведения — тоже одна на все ролики. */
  rate: number;
}

/** Скорости, которые предлагает плеер. */
export const RATES = [1, 1.25, 1.5, 2] as const;

const KEY = "montage.player.volume";
const DEFAULT: SavedVolume = { volume: 1, muted: false, rate: 1 };

function read(): SavedVolume {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT;
    const v = JSON.parse(raw) as Partial<SavedVolume>;
    const volume = typeof v.volume === "number" && v.volume >= 0 && v.volume <= 1 ? v.volume : 1;
    const rate = typeof v.rate === "number" && v.rate >= 0.25 && v.rate <= 4 ? v.rate : 1;
    return { volume, muted: v.muted === true, rate };
  } catch {
    return DEFAULT;
  }
}

let current: SavedVolume = read();
const listeners = new Set<() => void>();

export function getSavedVolume(): SavedVolume {
  return current;
}

export function saveVolume(next: Partial<SavedVolume>): void {
  const merged = { ...current, ...next };
  merged.volume = Math.min(1, Math.max(0, merged.volume));
  merged.rate = Math.min(4, Math.max(0.25, merged.rate));
  if (merged.volume === current.volume && merged.muted === current.muted && merged.rate === current.rate) return;
  current = merged;
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* хранилище недоступно — помним до перезагрузки */
  }
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Сохранённая громкость; все плееры на странице меняются вместе. */
export function useSavedVolume(): SavedVolume {
  return useSyncExternalStore(subscribe, getSavedVolume, getSavedVolume);
}

/**
 * Применить сохранённое к элементу <video> — на загрузке и при каждом
 * изменении. Сам элемент не трогаем, если там уже то же самое: иначе
 * volumechange вернулся бы к нам эхом.
 */
export function applyVolume(el: HTMLVideoElement | null, v: SavedVolume): void {
  if (!el) return;
  if (Math.abs(el.volume - v.volume) > 0.001) el.volume = v.volume;
  if (el.muted !== v.muted) el.muted = v.muted;
  if (el.playbackRate !== v.rate) el.playbackRate = v.rate;
}

/** Обработчик volumechange для плеера со встроенными кнопками браузера. */
export function rememberVolume(el: HTMLVideoElement): void {
  saveVolume({ volume: el.volume, muted: el.muted });
}
